import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { Icons } from '../icons/registry'

/**
 * The date field, with the calendar it always should have had.
 *
 * A trigger that reads like every other field in the house, and a popover
 * carrying a real month grid — themed, Monday-first, with a year jump beside
 * the month walk, because a date of birth is decades away from today and a
 * single chevron is not a journey anyone should take. The value is the ISO
 * string the contracts speak; nothing here invents a format of its own.
 *
 * The native date input is deliberately not used: its picker is the
 * browser's, not the house's, and it cannot be made to be.
 */

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

const pad = (n: number) => String(n).padStart(2, '0')
const toIso = (year: number, month: number, day: number) => `${year}-${pad(month + 1)}-${pad(day)}`

/** The parts of an ISO date, parsed by hand — `new Date('YYYY-MM-DD')` reads the string as UTC and lands a day off in the west. */
function parseIso(iso: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  if (month < 0 || month > 11 || day < 1 || day > 31) return null
  return { year, month, day }
}

function sameMonth(a: { year: number; month: number }, b: { year: number; month: number }): boolean {
  return a.year === b.year && a.month === b.month
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder,
  mode = 'date',
  invalid,
  className,
}: {
  id?: string
  /** The chosen date as `YYYY-MM-DD`, or the empty string for none. */
  value: string
  onChange: (iso: string) => void
  placeholder?: string
  mode?: 'date' | 'month'
  /** Reads as a field that currently refuses the answer. */
  invalid?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [panel, setPanel] = useState<'days' | 'months' | 'years'>('days')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const dialogId = useId()
  const [position, setPosition] = useState({ top: 0, left: 0, width: 280, maxHeight: 360 })

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const margin = 12
      const width = Math.min(280, window.innerWidth - margin * 2)
      const height = Math.min(panelRef.current?.scrollHeight || 340, window.innerHeight - margin * 2)
      const below = window.innerHeight - rect.bottom - margin - 8
      const above = rect.top - margin - 8
      const flip = below < height && above > below
      const maxHeight = Math.max(0, flip ? above : below)
      setPosition({
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        top: flip ? Math.max(margin, rect.top - Math.min(height, maxHeight) - 8) : rect.bottom + 8,
        width,
        maxHeight,
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, panel])
  const yearListRef = useRef<HTMLDivElement>(null)
  const parsed = value ? parseIso(mode === 'month' ? `${value}-01` : value) : null
  const today = useMemo(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() }
  }, [])
  // The grid opens on the value's month when there is one, otherwise today's.
  const [view, setView] = useState(() => ({ year: parsed?.year ?? today.year, month: parsed?.month ?? today.month }))

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node) && !panelRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      const target = panelRef.current?.querySelector<HTMLButtonElement>('[aria-current="true"], [aria-pressed="true"]')
        ?? panelRef.current?.querySelector<HTMLButtonElement>('button')
      target?.focus({ preventScroll: true })
    }
  }, [open, panel])

  const openTo = (iso: string) => {
    const parts = parseIso(mode === 'month' ? `${iso}-01` : iso)
    setView({ year: parts?.year ?? today.year, month: parts?.month ?? today.month })
    setPanel(mode === 'month' ? 'months' : 'days')
    setOpen(true)
  }

  useEffect(() => {
    if (panel !== 'years') return
    const selectedYear = yearListRef.current?.querySelector<HTMLElement>('[aria-current="true"]')
    selectedYear?.scrollIntoView?.({ block: 'center' })
  }, [panel])

  const step = (months: number) => {
    setView((current) => {
      const total = current.year * 12 + current.month + months
      return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 }
    })
  }

  // Six rows, always: the grid keeps its height through every month, so the
  // popover never jumps while it is open.
  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1)
    const lead = (first.getDay() + 6) % 7 // Monday-first
    const start = new Date(view.year, view.month, 1 - lead)
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)
      return {
        key: index,
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        outside: date.getMonth() !== view.month,
        iso: toIso(date.getFullYear(), date.getMonth(), date.getDate()),
      }
    })
  }, [view])

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        aria-controls={open ? dialogId : undefined}
        aria-invalid={invalid || undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openTo(value))}
        className={cn('wf-field flex items-center justify-between gap-2 text-left', invalid && 'wf-field-invalid')}
      >
        <span key={value} className={cn('wf-calendar-panel truncate', !parsed && 'text-muted-foreground/70')}>{parsed ? `${mode === 'date' ? `${parsed.day} ` : ''}${MONTHS[parsed.month]} ${parsed.year}` : placeholder ?? (mode === 'month' ? 'Choose a month' : 'Choose a date')}</span>
        <Icons.calendar size="sm" className="shrink-0 text-muted-foreground" />
      </button>

      {open ? createPortal(
        <div
          ref={panelRef}
          data-wf-popover
          id={dialogId}
          style={position}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null) && event.relatedTarget !== triggerRef.current) setOpen(false)
          }}
          role="dialog"
          aria-label={mode === 'month' ? 'Choose a month' : 'Choose a date'}
          className="wf-calendar-panel wf-frost fixed z-[200] overflow-y-auto overscroll-contain rounded-xl border border-hairline p-3 shadow-modal"
        >
          {panel === 'days' ? (
            <>
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Previous month"
                  onClick={() => step(-1)}
                  className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
                >
                  <Icons.chevronLeft size="sm" />
                </button>
                <button
                  type="button"
                  aria-label="Choose month and year"
                  onClick={() => setPanel('months')}
                  className="rounded-md px-2 py-1 text-caption font-medium text-foreground transition-colors duration-fast hover:bg-tint"
                >
                  {MONTHS[view.month]} {view.year}
                </button>
                <button
                  type="button"
                  aria-label="Next month"
                  onClick={() => step(1)}
                  className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
                >
                  <Icons.chevronLeft size="sm" className="rotate-180" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {WEEKDAYS.map((weekday) => (
                  <span key={weekday} className="flex h-7 items-center justify-center text-micro text-muted-foreground/70 uppercase">
                    {weekday}
                  </span>
                ))}
                {cells.map((cell) => {
                  const selected = parsed && sameMonth({ year: parsed.year, month: parsed.month }, { year: cell.year, month: cell.month }) && parsed.day === cell.day
                  const isToday = sameMonth(today, cell) && today.day === cell.day
                  return (
                    <button
                      key={cell.key}
                      type="button"
                      aria-label={`${cell.day} ${MONTHS[cell.month]} ${cell.year}`}
                      aria-pressed={Boolean(selected)}
                      onClick={() => {
                        onChange(cell.iso)
                        setOpen(false)
                        triggerRef.current?.focus()
                      }}
                      className={cn(
                        'flex h-8 items-center justify-center rounded-full text-caption transition-colors duration-fast',
                        cell.outside ? 'text-muted-foreground/40' : 'text-foreground',
                        !selected && 'hover:bg-tint',
                        isToday && !selected && 'ring-1 ring-inset ring-hairline',
                        selected && 'bg-foreground font-medium text-surface',
                      )}
                    >
                      {cell.day}
                    </button>
                  )
                })}
              </div>
            </>
          ) : panel === 'months' ? (
            <>
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  disabled={mode === 'month'}
                  onClick={() => setPanel('days')}
                  className="rounded-md px-2 py-1 text-caption text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
                >
                  {mode === 'month' ? 'Choose month' : 'Back to calendar'}
                </button>
                <button
                  type="button"
                  aria-label="Choose year"
                  onClick={() => setPanel('years')}
                  className="rounded-md px-2 py-1 text-caption font-medium text-foreground transition-colors duration-fast hover:bg-tint"
                >
                  {view.year}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {MONTHS.map((month, index) => (
                  <button
                    key={month}
                    type="button"
                    aria-pressed={view.month === index}
                    onClick={() => {
                      setView((current) => ({ ...current, month: index }))
                      if (mode === 'month') {
                        onChange(`${view.year}-${pad(index + 1)}`)
                        setOpen(false)
                        triggerRef.current?.focus()
                      } else setPanel('days')
                    }}
                    className={cn(
                      'rounded-lg px-2 py-3 text-caption transition-colors duration-fast',
                      view.month === index ? 'bg-foreground font-medium text-surface' : 'text-foreground hover:bg-tint',
                    )}
                  >
                    {month.slice(0, 3)}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setPanel('months')}
                  className="rounded-md px-2 py-1 text-caption text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
                >
                  Back to months
                </button>
                <span className="px-2 py-1 text-caption font-medium text-foreground">Choose year</span>
              </div>
              <div ref={yearListRef} className="grid max-h-56 grid-cols-4 gap-1 overflow-y-auto overscroll-contain pr-1">
                {Array.from({ length: Math.max(0, today.year + 10 - 1900 + 1) }, (_, index) => 1900 + index).map((year) => (
                  <button
                    key={year}
                    type="button"
                    aria-current={view.year === year ? 'true' : undefined}
                    onClick={() => {
                      setView((current) => ({ ...current, year }))
                      setPanel('months')
                    }}
                    className={cn(
                      'rounded-md py-2 text-caption transition-colors duration-fast',
                      view.year === year ? 'bg-foreground font-medium text-surface' : 'text-foreground hover:bg-tint',
                    )}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>,
        document.body,
      ) : null}
    </div>
  )
}
